import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../DashBoardComponents/Navbar";
import { POSTS } from "./BlogData";

function BlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const darkMode = sessionStorage.getItem("dox-theme") === "dark";

  const post = POSTS.find((p) => String(p.id) === String(id));

  const pageBg = darkMode ? "#0a0800" : "#f4f7fa";
  const cardBg = darkMode ? "#14110a" : "#fff";
  const cardBorder = darkMode
    ? "1px solid rgba(200,150,30,0.15)"
    : "1px solid rgba(0,0,0,0.06)";
  const textPrimary = darkMode ? "#f5e6b0" : "#071a3d";
  const textSecondary = darkMode ? "rgba(245,230,176,0.65)" : "#64748b";
  const accent = darkMode ? "#f0c040" : "#189FAA";
  const bodyText = darkMode ? "rgba(245,230,176,0.85)" : "#334155";

  // Post not found — id may be wrong or the page hit directly without matching data
  if (!post) {
    return (
      <>
        <Navbar />
        <div
          style={{
            background: pageBg,
            minHeight: "100vh",
            paddingTop: "120px",
            textAlign: "center",
          }}
        >
          <h2 style={{ color: textPrimary, fontFamily: "Outfit, sans-serif" }}>
            Post not found
          </h2>
          <button
            onClick={() => navigate("/blog")}
            style={{
              marginTop: "16px",
              border: "none",
              borderRadius: "999px",
              padding: "10px 22px",
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
              background: "linear-gradient(135deg,#0d3b3e,#189FAA,#1ec98f)",
            }}
          >
            Back to Blog
          </button>
        </div>
      </>
    );
  }

  const Icon = post.icon;
  const related = POSTS.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <>
      <Navbar />
      <div style={{ background: pageBg, minHeight: "100vh" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
          .dox-blog-detail * { font-family: 'Outfit', sans-serif; }
          .dox-back-link { transition: gap 0.2s ease; }
          .dox-back-link:hover { gap: 8px; }
          .dox-related-card { transition: transform 0.25s ease, box-shadow 0.25s ease; }
          .dox-related-card:hover { transform: translateY(-4px); box-shadow: 0 14px 32px rgba(24,159,170,0.16); }
          .dox-article-bullets { margin: 0 0 22px; padding: 0; list-style: none; }
          .dox-article-bullets li {
            position: relative;
            padding-left: 26px;
            margin-bottom: 12px;
            font-size: 16px;
            line-height: 1.7;
            color: ${bodyText};
          }
          .dox-article-bullets li::before {
            content: "";
            position: absolute;
            left: 4px;
            top: 10px;
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${accent};
          }
        `}</style>

        <div className="dox-blog-detail" style={{ paddingTop: "90px" }}>
          {/* ── Hero cover ── */}
          <div
            style={{
              position: "relative",
              height: "260px",
              overflow: "hidden",
              background: post.gradient,
            }}
          >
            <img
              src={post.image}
              alt={post.title}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 20%",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(7,26,61,0.15) 0%, rgba(7,26,61,0.85) 100%)",
              }}
            />
          </div>

          {/* ── Title block (below the cover image, not overlapping) ── */}
          <div
            style={{
              maxWidth: "820px",
              margin: "0 auto",
              padding: "22px 24px 0",
            }}
          >
            <button
              onClick={() => navigate("/blog")}
              className="dox-back-link"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "16px",
                background: darkMode
                  ? "rgba(240,192,64,0.12)"
                  : "rgba(24,159,170,0.08)",
                border: "none",
                borderRadius: "999px",
                padding: "8px 16px",
                color: accent,
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <ArrowLeft size={14} /> Back to Blog
            </button>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "14px",
              }}
            >
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "8px",
                  background: darkMode
                    ? "rgba(240,192,64,0.12)"
                    : "rgba(24,159,170,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon size={16} color={accent} />
              </div>
              <span
                style={{
                  color: textSecondary,
                  fontSize: "12.5px",
                  fontWeight: 600,
                }}
              >
                {post.date}
              </span>
            </div>

            <h1
              style={{
                fontSize: "32px",
                fontWeight: 800,
                color: textPrimary,
                lineHeight: 1.25,
                margin: 0,
              }}
            >
              {post.title}
            </h1>
          </div>

          {/* ── Author strip — modern gradient ring avatar ── */}
          <div
            style={{
              maxWidth: "820px",
              margin: "0 auto",
              padding: "24px 24px 0",
              display: "flex",
              alignItems: "center",
              gap: "14px",
            }}
          >
            <div
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                padding: "3px",
                flexShrink: 0,
                background: `linear-gradient(135deg, ${accent}, #7c3aed 65%, #ec4899)`,
                boxShadow: darkMode
                  ? "0 6px 16px rgba(240,192,64,0.35)"
                  : "0 6px 16px rgba(24,159,170,0.3)",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `3px solid ${pageBg}`,
                  background: accent,
                }}
              >
                <img
                  src={post.authorImage}
                  alt={post.author}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            </div>
            <div>
              <div
                style={{
                  fontWeight: 700,
                  color: textPrimary,
                  fontSize: "14.5px",
                }}
              >
                {post.author}
              </div>
              <div style={{ color: textSecondary, fontSize: "12.5px" }}>
                {post.date}
              </div>
            </div>
          </div>

          {/* ── Article body ── */}
          <div
            style={{
              maxWidth: "820px",
              margin: "0 auto",
              padding: "28px 24px 60px",
            }}
          >
            {post.content.map((block, i) => {
              if (block.type === "heading") {
                return (
                  <h3
                    key={i}
                    style={{
                      fontSize: "21px",
                      fontWeight: 700,
                      color: textPrimary,
                      margin: "34px 0 14px",
                      lineHeight: 1.4,
                    }}
                  >
                    {block.text}
                  </h3>
                );
              }

              if (block.type === "bullets") {
                return (
                  <ul key={i} className="dox-article-bullets">
                    {block.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                );
              }

              return (
                <p
                  key={i}
                  style={{
                    fontSize: "16px",
                    lineHeight: 1.85,
                    color: bodyText,
                    marginBottom: "20px",
                  }}
                >
                  {block.text}
                </p>
              );
            })}
          </div>

          {/* ── Related posts ── */}
          {related.length > 0 && (
            <div
              style={{
                maxWidth: "1180px",
                margin: "0 auto",
                padding: "0 24px 70px",
              }}
            >
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: textPrimary,
                  marginBottom: "22px",
                }}
              >
                More from Doxzon
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: "20px",
                }}
              >
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/blog/${r.id}`}
                    className="dox-related-card"
                    style={{
                      textDecoration: "none",
                      background: cardBg,
                      border: cardBorder,
                      borderRadius: "12px",
                      overflow: "hidden",
                      display: "block",
                    }}
                  >
                    <div
                      style={{
                        height: "120px",
                        position: "relative",
                        background: r.gradient,
                      }}
                    >
                      <img
                        src={r.image}
                        alt={r.title}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <div style={{ padding: "14px 16px" }}>
                      <div
                        style={{
                          fontSize: "13.5px",
                          fontWeight: 700,
                          color: textPrimary,
                          lineHeight: 1.4,
                        }}
                      >
                        {r.title}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default BlogDetail;
